import { forwardRef } from 'react'

import { Radio as RadioPrimitive } from '@base-ui/react/radio'
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Text, type TypographyVariant } from '../../atoms/text/text'

const radioItemVariants = tv({
  base: [
    'group peer inline-flex shrink-0 items-center justify-center',
    'border-surface-body rounded-full border-2',
    'cursor-pointer transition-colors',
    'focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'data-[checked]:border-surface-title-50',
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'
  ],
  variants: {
    size: {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

const indicatorDotVariants = tv({
  base: 'bg-surface-title-50 rounded-full',
  variants: {
    size: {
      sm: 'size-2',
      md: 'size-2.5',
      lg: 'size-3'
    }
  },
  defaultVariants: { size: 'md' }
})

const labelSizeVariant = {
  sm: 'caption-lg',
  md: 'body-md',
  lg: 'body-xl'
} as const satisfies Record<string, TypographyVariant>

export type RadioGroupItemSize = NonNullable<
  VariantProps<typeof radioItemVariants>['size']
>

export type RadioGroupProps = React.ComponentProps<
  typeof RadioGroupPrimitive
> & {
  className?: string
  orientation?: 'horizontal' | 'vertical'
}

export type RadioGroupItemProps = React.ComponentProps<
  typeof RadioPrimitive.Root
> &
  VariantProps<typeof radioItemVariants> & {
    className?: string
    label?: string
    children?: React.ReactNode
  }

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, orientation = 'vertical', ...props }, ref) => (
    <RadioGroupPrimitive
      ref={ref}
      data-slot='radio-group'
      data-orientation={orientation}
      className={cn(
        'flex gap-3',
        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
        className
      )}
      {...props}
    />
  )
)
RadioGroup.displayName = 'RadioGroup'

export const RadioGroupItem = forwardRef<
  HTMLButtonElement,
  RadioGroupItemProps
>(({ size = 'md', className, label, children, ...props }, ref) => {
  const radio = (
    <RadioPrimitive.Root
      ref={ref}
      data-slot='radio-group-item'
      data-size={size}
      aria-label={label && !props['aria-label'] ? label : props['aria-label']}
      className={cn(radioItemVariants({ size }), className)}
      {...props}
    >
      <RadioPrimitive.Indicator className={indicatorDotVariants({ size })} />
    </RadioPrimitive.Root>
  )

  if (label) {
    return (
      <label className='inline-flex cursor-pointer items-center gap-2 has-[[data-disabled]]:cursor-not-allowed'>
        {radio}
        <Text
          as='span'
          variant={labelSizeVariant[size ?? 'md']}
          className='text-surface-title-50 select-none'
        >
          {label}
        </Text>
      </label>
    )
  }

  if (children) {
    return (
      <>
        {radio}
        {children}
      </>
    )
  }

  return radio
})
RadioGroupItem.displayName = 'RadioGroupItem'
